/**
 * SyncStatusIndicator — small "Updating…" badge in the app shell.
 *
 * D5: visible while
 *   (a) a catch-up pull is in flight (`pullState === 'pulling'`) after a
 *       tab-return, OR
 *   (b) the SSE connection is down (`sseState !== 'open'`).
 *
 * Hidden during steady-state foreground, including routine 30 s background
 * polls — the indicator is meant to communicate "we know about a gap and
 * are catching up", not "every periodic poll".
 *
 * The hook caller (`useSyncEngine`) owns the state; this component only
 * renders.
 */

import type { CSSProperties } from 'react';
import type { PullLifecycleState } from '../lib/syncEngineProvider';
import type { SseConnectionState } from '../lib/sseSubscriber';

export interface SyncStatusIndicatorProps {
  pullState: PullLifecycleState;
  sseState: SseConnectionState;
  /**
   * Override visibility — primarily a test hook. When provided, replaces
   * the pull-state + SSE-state derivation.
   */
  forceVisible?: boolean;
}

const indicatorStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-subtle)',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
};

const dotStyle: CSSProperties = {
  display: 'inline-block',
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '50%',
  backgroundColor: 'var(--color-accent-primary)',
  // Subtle pulse so the badge reads as "active" rather than a static label.
  animation: 'shopista-pulse 1.4s ease-in-out infinite',
};

export function SyncStatusIndicator({
  pullState,
  sseState,
  forceVisible,
}: SyncStatusIndicatorProps): JSX.Element | null {
  const isCatchingUp = pullState === 'pulling';
  const isDisconnected = sseState !== 'open';
  const visible = forceVisible ?? (isCatchingUp || isDisconnected);

  if (!visible) return null;

  return (
    <div
      style={indicatorStyle}
      role="status"
      aria-live="polite"
      data-testid="sync-status-indicator"
    >
      <span style={dotStyle} aria-hidden="true" />
      Updating…
    </div>
  );
}
